/**
 *  Replicator
 *
 *  Created by Legman on 22/02/2017.
 *
 */

import Actor        from "./Actor";
import Audio        from "./Audio";
import AudioParams  from "./AudioParams";
import Common       from "./Common";

/**
 * @class
 */
export default class Replicator extends Actor {
    constructor() {
        const assets = Common.assets;
        super(assets.getTexture("replicator_support"));

        /**
         * @member {number}
         */
        this.moveSpeed = 0;

        /**
         * @member {{min: number, max: number}}
         */
        this.moveSpeedRange = {min: 540, max: 540};

        /**
         * @member {{lower: number, upper: number}}
         */
        this.moveSpeedLimit = {lower: 440, upper: 1200};

        /**
         * @member {{lower: number, upper: number}}
         */
        this.moveSpeedMulti = {lower: 0.92, upper: 1.24};

        /**
         * @member {number}
         */
        this.moveRange = p3.View.width * 0.32;

        /**
         * @member {boolean}
         */
        this.moving = true;

        /**
         * @member {number}
         * @private
         */
        this.direction = Math.random() < 0.5 ? 1 : -1;

        /**
         * @member {PIXI.Point}
         */
        this.startPos = new PIXI.Point();

        /**
         * @member {p3.MovieClip}
         * @private
         */
        this._wire = null;

        /**
         * @member {p3.MovieClip}
         * @private
         */
        this._head = null;

        /**
         * @member {PIXI.Sprite}
         * @private
         */
        this._beam = null;

        /**
         * @member {boolean}
         * @private
         */
        this._charged = false;

        /**
         * @member {Audio}
         * @private
         */
        this._moveSFX = null;

        /**
         * @member {PIXI.particles.Emitter}
         * @private
         */
        this._emitter = null;
    }

    init() {
        super.init();

        this.startPos.x = this.x;
        this.startPos.y = this.y;

        const assets = Common.assets;

        let sequence = new p3.MovieClipSequence();
        sequence.addTextures([assets.getTexture("replicator_wire_off"), assets.getTexture("replicator_wire_on")]);

        this._wire = new p3.MovieClip(sequence);
        this._wire.anchor = new PIXI.Point(0.5, 0.5);
        this._wire.gotoAndStop(1);
        this.addChildAt(this._wire, 0);

        this._glow = new PIXI.Sprite(assets.getTexture("replicator_front_on"));
        this._glow.anchor = new PIXI.Point(0.5, 0.5);
        this.addChildAt(this._glow, 0);

        this._head = new PIXI.Sprite(assets.getTexture("replicator_front_off"));
        this._head.anchor = new PIXI.Point(0.5, 0.5);
        this.addChild(this._head);

        sequence = new p3.MovieClipSequence();
        sequence.addTextures([assets.getTexture("replicator_lightbulb_off"), assets.getTexture("replicator_lightbulb_on")]);

        this._head.bulb = new p3.MovieClip(sequence);
        this._head.bulb.animationSpeed = 1 / 8;
        this._head.bulb.looping = true;
        this._head.bulb.anchor = new PIXI.Point(0.5, 0.5);
        this._head.addChild(this._head.bulb);

        sequence = new p3.MovieClipSequence();
        sequence.addTextures([PIXI.Texture.EMPTY, assets.getTexture("replicator_light_001")]);

        this._head.light1 = new p3.MovieClip(sequence);
        this._head.light1.animationSpeed = 1 / 24;
        this._head.light1.looping = true;
        this._head.light1.anchor = new PIXI.Point(0.5, 0.5);
        this._head.light1.play();
        this._head.addChild(this._head.light1);

        sequence = new p3.MovieClipSequence();
        sequence.addTextures([PIXI.Texture.EMPTY, assets.getTexture("replicator_light_002")]);

        this._head.light2 = new p3.MovieClip(sequence);
        this._head.light2.animationSpeed = 1 / 24;
        this._head.light2.looping = true;
        this._head.light2.anchor = new PIXI.Point(0.5, 0.5);
        this._head.light2.gotoAndPlay(1);
        this._head.addChild(this._head.light2);

        this._beam = new PIXI.Sprite(assets.getTexture("replicator_back"));
        this._beam.y = 374;
        this._beam.anchor = new PIXI.Point(0.5, 0.5);
        this._beam.visible = false;
        this.addChildAt(this._beam, 0);

        this.charged = true;

        this.mixUpSpeed();
        this.playMoveSFX();

        const holder = new PIXI.Container();
        this.parent.addChild(holder);

        const config = assets.getJSON("replicator_001");
        this._emitter = new PIXI.particles.Emitter(holder, [assets.getTexture("particle_replicator_001")], config);
        this._emitter.emit = false;
    }

    /**
     * @param {boolean} [destroyChildren]
     */
    destroy(destroyChildren) {
        this._moveSFX && this._moveSFX.stop();

        super.destroy(destroyChildren);
    }

    update() {
        super.update();

        this.moving && (this.x += this.moveSpeed * this.direction * deltaTime);

        if (Math.abs(this.x - this.startPos.x) > this.moveRange) {
            this.x = this.startPos.x + this.moveRange * this.direction;
            this.direction *= -1;

            this.playMoveSFX();
        }

        this.moving && (this._head.y = 216 + p3.Utils.randomInt(1, -1));
        this._glow.y = this._head.y;

        this._emitter.updateOwnerPos(this.x, this.y + 360);
        this._emitter.update(deltaTime);
    }

    animate() {
        this._beam.alpha = 1;
        this._beam.visible = true;
        this._emitter.emit = true;

        TweenMax.to(this._beam, 0.34, {
            delay: 0.44,
            alpha: 0,
            ease: Power3.easeInOut,
            onComplete: function() {
                this._beam.visible = false;
                this._emitter.emit = false;
            },
            onCompleteScope: this
        });
    }

    mixUpSpeed() {
        this.moveSpeed = p3.Utils.randomInt(this.moveSpeedRange.max, this.moveSpeedRange.min);
        console.log("movespeed " + this.moveSpeed);
    }

    increaseSpeedRange() {
        this.moveSpeedRange.min = Math.max(this.moveSpeedLimit.lower, this.moveSpeedRange.min * this.moveSpeedMulti.lower);
        this.moveSpeedRange.max = Math.min(this.moveSpeedLimit.upper, this.moveSpeedRange.max * this.moveSpeedMulti.upper);
        console.log("speedrange min: " + this.moveSpeedRange.min + " max: " + this.moveSpeedRange.max);
    }

    playMoveSFX() {
        this.stopMoveSFX();

        const params = new AudioParams();
        params.loop = true;

        let sounds;
        if (this.direction == -1) {
            sounds = "sfx_arm_left_00";
        } else if (this.direction == 1) {
            sounds = "sfx_arm_right_00";
        }
        this._moveSFX = Common.audio.playSound(sounds, params);
    }

    playMoveUpSFX() {
        this.stopMoveSFX();

        const params = new AudioParams();
        params.loop = true;

        this._moveSFX = Common.audio.playSound("sfx_arm_left_00", params);
    }

    stopMoveSFX() {
        this._moveSFX && this._moveSFX.stop();
        this._moveSFX = null;
    }

    /**
     * @returns {boolean}
     */
    get charged() {
        return this._charged;
    }

    /**
     * @param {boolean} value
     */
    set charged(value) {
        this._charged = value;

        if (this._charged) {
            this._head.bulb.gotoAndPlay(0);

            this._glow.alpha = 0;
            TweenMax.killTweensOf(this._glow);
            TweenMax.to(this._glow, 0.24, {
                alpha: 1,
                ease: Power2.easeInOut,
                onStart: function() {
                    this._glow.visible = true;
                },
                onStartScope: this
            });
        } else {
            this._head.bulb.gotoAndStop(0);
            this._glow.visible = false;
        }
    }
}
